import { Alert, Button, Flex, Input } from 'antd';
import { ContactRelationEnum, useCreateContactMutation } from 'api/ContactsApi';
import {
  ChatHistoryModelItem,
  ChatHistoryUserItem,
  FlowCommunicateResponse,
  useCommunicateFlowMutation,
  useLoadFlowHistoryQuery
} from 'api/FlowAiApi';
import { useGenerateMessageMutation } from 'api/MessageAiApi';
import { MessageStatusEnum, MessageTypeEnum, useUpsertMessageMutation } from 'api/MessagesApi';
import ModelBubble from 'components/Message/ChatComponent/ModelBubble/ModelBubble';
import ModelBubbleLoading from 'components/Message/ChatComponent/ModelBubble/ModelBubbleLoading';
import SavingFooter from 'components/Message/ChatComponent/SavingFooter/SavingFooter';
import UserBubble from 'components/Message/ChatComponent/UserBubble/UserBubble';
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { BsCheckLg, BsStars } from 'react-icons/bs';
import { useParams } from 'react-router-dom';
import { getError } from 'utils/utils';
import './ChatComponent.css';

type AllProps = {
  chatId?: string | null;
  onExit?: () => void;
};

function ChatComponent({ chatId, onExit }: AllProps) {
  const { chatId: chatIdFromParams } = useParams();
  chatId = chatId || chatIdFromParams;

  const [status, setStatus] = useState<'compose' | 'saving' | 'saved'>('compose');
  const [draftTitle, setDraftTitle] = useState<string>('');
  const [draftText, setDraftText] = useState<string>('');
  const [input, setInput] = useState<string>('');
  const [data, setData] = useState<(ChatHistoryModelItem | ChatHistoryUserItem)[]>([]);
  const lastResponse = useRef<FlowCommunicateResponse | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatFooterRef = useRef<HTMLDivElement>(null);

  const { data: historyData, isLoading: isLoadingHistory } = useLoadFlowHistoryQuery(
    { id: chatId! },
    { skip: !chatId }
  );
  const [communicateFlow, { isLoading, isError, error }] = useCommunicateFlowMutation();
  const [generateMessage, { isLoading: generateLoading }] = useGenerateMessageMutation();
  const [createMessage, { isLoading: createMessageLoading }] = useUpsertMessageMutation();
  const [createContact, { isLoading: createContactLoading }] = useCreateContactMutation();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end'
    });
  };

  useEffect(() => {
    const footerElement = chatFooterRef.current;
    if (!footerElement) return;

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(() => {
        scrollToBottom();
      }, 50);
    });

    resizeObserver.observe(footerElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      scrollToBottom();
    }, 50);
  }, [data, isLoading, generateLoading, draftText]);

  const onSubmit = async (answer?: string) => {
    const finalAnswer = answer || input.trim();

    try {
      setData((prev) => [...prev, { sender: 'user', message: finalAnswer, createdAt: new Date() }]);
      setInput('');
      setDraftText('');

      const response = await communicateFlow({
        id: chatId!,
        questionId:
          lastResponse.current?.chatItem.message.followup_for || lastResponse.current?.chatItem.message.id || '',
        answer: finalAnswer
      }).unwrap();
      lastResponse.current = response;
      setData((prev) => [...prev, response.chatItem]);
    } catch (e) {
      console.error(e);
    }
  };

  const generateMessageRequest = useCallback(async () => {
    try {
      const { title, content } = await generateMessage({
        tones: lastResponse.current?.currentState.answers.tone ? [lastResponse.current?.currentState.answers.tone] : [],
        // emotionFocus?: string;
        // memory?: string;
        // closing?: string;
        otherInfo: lastResponse.current?.currentState.answers.extra,
        type: MessageTypeEnum.Text,
        // TODO: hardcoded value for eventType
        eventType: 'birthday',
        recipients: lastResponse.current?.currentState.answers.recipientName
          ? [
              {
                name: lastResponse.current?.currentState.answers.recipientName,
                relationship: lastResponse.current?.currentState.answers.recipientRelationship || ''
              }
            ]
          : []
      }).unwrap();

      setDraftTitle(title);
      setDraftText(content);
    } catch (e) {
      console.error(e);
    }
  }, [generateMessage]);

  useEffect(() => {
    if (data.length > 0) {
      const lastMessage = data[data.length - 1];
      if (lastMessage.sender === 'model' && lastMessage.message.kind === 'final') {
        void generateMessageRequest();
      }
    }
  }, [data, generateMessageRequest]);

  const handleSaveMessage = async (title: string, recipientEmail?: string) => {
    const currentState = lastResponse.current?.currentState.answers;
    if (!currentState) {
      return;
    }

    try {
      let recipientId = currentState.recipientId;
      if (!currentState.recipientId && currentState.recipientName && recipientEmail) {
        const reps = await createContact({
          name: currentState.recipientName,
          email: recipientEmail,
          phone: '',
          relationship: currentState.recipientRelationship as ContactRelationEnum
        }).unwrap();

        recipientId = reps.id;
      }

      await createMessage({
        name: title,
        content: draftText,
        type: MessageTypeEnum.Text,
        status: MessageStatusEnum.Draft,
        contactIds: recipientId ? [recipientId] : [],
        eventType: 'birthday',
        deliveryYear: 2027,
        deliveryMonth: 4,
        deliveryDay: 20
      }).unwrap();
      setStatus('saved');
    } catch (e) {
      console.error(e);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const target = e.target as HTMLTextAreaElement;
        const start = target.selectionStart;
        const end = target.selectionEnd;
        const value = target.value;

        const newValue = value.substring(0, start) + '\n' + value.substring(end);
        setInput(newValue);

        setTimeout(() => {
          target.selectionStart = target.selectionEnd = start + 1;
          target.focus();

          const lineHeight = parseInt(getComputedStyle(target).lineHeight) || 20;
          const lines = target.value.substring(0, start + 1).split('\n').length;
          target.scrollTop = Math.max(0, (lines - 1) * lineHeight - target.clientHeight / 2);
        }, 0);
      } else {
        e.preventDefault();
        if (input.trim().length > 0 && !isLoading && !generateLoading) {
          void onSubmit();
        }
      }
    }
  };

  useEffect(() => {
    if (historyData?.length) {
      lastResponse.current = {
        chatItem: historyData.filter((d) => d.sender === 'model').pop() || null,
        currentState: {
          answers: {}
        }
      };
      setData(historyData);
    }
  }, [historyData]);

  return (
    <div className="chat-component">
      <Flex justify="space-between" vertical flex="1">
        <div className="message-list">
          {isLoadingHistory ? (
            <div>Loading conversation...</div>
          ) : (
            <>
              {data.map((data, index, arr) =>
                data.sender === 'model' ? (
                  <ModelBubble
                    key={index}
                    data={data}
                    isLastItem={arr.length - 1 === index}
                    onQuickAnswer={(answer) => onSubmit(answer)}
                  />
                ) : (
                  <UserBubble key={index} data={data} />
                )
              )}
              {isLoading && <ModelBubbleLoading />}
              {generateLoading && <ModelBubbleLoading text="Generating message" />}
              {draftText && (
                <Flex gap={15} vertical>
                  <div className="generated-title">
                    <BsStars /> Generated message
                  </div>
                  <Input.TextArea
                    value={draftText}
                    disabled={status === 'saved'}
                    onChange={(e) => setDraftText(e.target.value)}
                    autoSize={{ minRows: 6, maxRows: 10 }}
                  ></Input.TextArea>

                  {status === 'compose' && (
                    <div>
                      <Button onClick={() => setStatus('saving')}>Save</Button>
                    </div>
                  )}
                </Flex>
              )}
              <div ref={messagesEndRef}>&nbsp;</div>
            </>
          )}
        </div>

        <div className="chat-footer" ref={chatFooterRef}>
          {isError && <Alert type="error" message={getError(error) || ''} showIcon />}

          {status === 'saved' && (
            <Alert
              message={
                <div className="success-block">
                  <BsCheckLg size={50} color={'green'} />
                  <div className="success-title">Message saved successfully!</div>
                  {onExit && <Button onClick={onExit}>Exit</Button>}
                </div>
              }
              type="success"
            />
          )}
          {status === 'saving' && (
            <SavingFooter
              initTitle={draftTitle}
              isRecipientExists={Boolean(lastResponse.current?.currentState.answers.recipientId)}
              onSave={handleSaveMessage}
              isLoading={createMessageLoading || createContactLoading}
            />
          )}
          {status === 'compose' && (
            <Flex justify="space-between" gap="small" align="center">
              <Input.TextArea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message here..."
                autoSize={{ minRows: 1, maxRows: 4 }}
              />
              <Button
                type="primary"
                onClick={() => onSubmit()}
                disabled={input.trim().length === 0 || isLoading || generateLoading}
              >
                Submit
              </Button>
            </Flex>
          )}
        </div>
      </Flex>
    </div>
  );
}
export default ChatComponent;
